import { ListItem, ListItemIcon, ListItemText } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import { useDrag, useDrop } from 'react-dnd';
import {
  Wall,
  WallFolderWithRelations,
  WallWindowRelations,
  WeatherWall,
} from 'types';
import { WallFolderWithWeatherRelations } from 'types/models/WallFolder';

import { useUpdateWall } from 'features/walls/api/updateWall';
import { useUpdateWeatherWall } from 'features/weatherWalls/api/updateWeatherWall';
import { queryClient } from 'lib/react-query';

interface Props {
  wall: Wall | WallWindowRelations;
  selected: boolean;
  handleWallClicked: (id: WeatherWall) => void;
  folder?: WallFolderWithRelations | WallFolderWithWeatherRelations;
}

const WallListItem = ({ wall, selected, handleWallClicked, folder }: Props) => {
  const updateWallMutation = useUpdateWall({});
  const updateWeatherWallMutation = useUpdateWeatherWall({});
  const [, drag] = useDrag(() => ({
    type: 'WALL',
    item: { name: wall.id, type: 'WALL' },
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
  }));
  const onDrop = async (item: { name: string }) => {
    if (
      wall.folder_id &&
      wall.folder_position !== undefined &&
      wall.folder_position !== null &&
      folder
    ) {
      if ('walls' in folder) {
        await updateWallMutation.mutateAsync({
          data: {
            folder_id: wall.folder_id,
            folder_position: wall.folder_position,
          },
          wallId: Number(item.name),
        });
        for await (const otherWall of folder.walls) {
          if (
            otherWall.folder_position !== null &&
            otherWall.folder_position !== undefined &&
            wall.folder_position <= otherWall.folder_position &&
            otherWall.id !== Number(item.name)
          ) {
            await updateWallMutation.mutateAsync({
              data: {
                folder_position: otherWall.folder_position + 1,
              },
              wallId: Number(otherWall.id),
            });
          }
        }
        queryClient.invalidateQueries(['folders']);
        queryClient.invalidateQueries(['walls']);
      } else if ('weatherWalls' in folder) {
        await updateWeatherWallMutation.mutateAsync({
          data: {
            folder_id: wall.folder_id,
            folder_position: wall.folder_position,
          },
          wallId: Number(item.name),
        });
        for await (const otherWall of folder.weatherWalls) {
          if (
            otherWall.folder_position !== null &&
            otherWall.folder_position !== undefined &&
            wall.folder_position <= otherWall.folder_position &&
            otherWall.id !== Number(item.name)
          ) {
            await updateWeatherWallMutation.mutateAsync({
              data: {
                folder_position: otherWall.folder_position + 1,
              },
              wallId: Number(otherWall.id),
            });
          }
        }
        queryClient.invalidateQueries(['weatherFolders']);
        queryClient.invalidateQueries(['weatherWalls']);
      }
    }
  };
  const [{ isOver }, drop] = useDrop({
    accept: 'WALL',
    drop: onDrop,
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });
  return (
    <div ref={drop}>
      <ListItem
        button
        selected={selected || isOver}
        onClick={() => handleWallClicked(wall)}
        ref={drag}
      >
        {wall.synched && (
          <ListItemIcon>
            <PublicIcon />
          </ListItemIcon>
        )}
        <ListItemText sx={{ pl: folder ? '2rem' : undefined }}>
          {wall.name}
        </ListItemText>
      </ListItem>
    </div>
  );
};

export default WallListItem;
